/* eslint-disable react-hooks/exhaustive-deps */
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { EditIcon, TrashAltIcon } from "assets/icons";
import { Media } from "components";
import { differenceInDays } from "helpers";
import { t } from "i18next";
import { useRefetchQuery } from "libs/reactQuery";
import { formattedDate, OfferStatus } from "modules/offers";
import moment from "moment";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState
} from "react";
import toast from "react-hot-toast";
import { RowDragHandleCell, TableItemActionsControl } from "ui";

import { useUpdateBanner } from "../apis";
import { IBanner } from "../banners.types";

const columnHelper = createColumnHelper<IBanner>();

interface ColumnProps {
  isDragDisabled?: boolean;
  onOpenEdit: () => void;
  onOpenDelete: () => void;
  setBannerId: Dispatch<SetStateAction<string | undefined>>;
}

const useBannersTable = ({
  isDragDisabled,
  onOpenEdit,
  onOpenDelete,
  setBannerId
}: ColumnProps) => {
  const refetchBanners = useRefetchQuery(["banners"]);

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const [openedItemStatusesIndex, setOpenedItemStatusesIndex] = useState<
    number | null
  >(null);

  const { mutate: onUpdateBannerStatus } = useUpdateBanner({
    onSuccess: () => {
      refetchBanners();
      setOpenedItemStatusesIndex(null);
      toast.success(t("Banner status was updated successfully"));
    }
  });

  const handleMenuButtonClick = useCallback(
    (
      cell: CellContext<IBanner, string | undefined>,
      handleOpenModal: () => void
    ) => {
      return () => {
        handleOpenModal();
        setBannerId(cell.getValue());
      };
    },
    [setBannerId]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.title, {
        id: "dragHandle",
        header: "",
        enableSorting: false,
        size: 3,
        minSize: 3,
        cell: (cell) => (
          <RowDragHandleCell
            rowId={cell.row.id}
            isDragDisabled={isDragDisabled}
          />
        )
      }),
      columnHelper.accessor((row) => row.title, {
        id: "Title",
        header: t("Banner"),
        size: 22,
        minSize: 22,
        cell: (cell) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              fontWeight: "700"
            }}
          >
            <Media
              mediaFile={cell.row.original.image}
              withVideoLabel
              style={{
                width: 136,
                height: 52,
                borderRadius: "4px"
              }}
            />
            <p>{cell.getValue()}</p>
          </div>
        )
      }),
      columnHelper.accessor((row) => row.from, {
        id: "From",
        header: t("From"),
        size: 9,
        minSize: 9,
        cell: (cell) => formattedDate(cell.getValue())
      }),
      columnHelper.accessor((row) => row.to, {
        id: "To",
        header: t("To"),
        size: 9,
        minSize: 9,
        cell: (cell) => formattedDate(cell.getValue())
      }),
      columnHelper.accessor((row) => row.from, {
        id: "daysLeft",
        header: "Days left",
        enableSorting: false,
        size: 9,
        minSize: 9,
        cell: (cell) =>
          differenceInDays(moment().toISOString(), cell.row.original.to)
      }),
      columnHelper.accessor((row) => row.createdAt, {
        id: "CreatedAt",
        header: t("Date created"),
        size: 10,
        minSize: 10,
        cell: (cell) => formattedDate(cell.getValue())
      }),
      columnHelper.accessor((row) => row.status, {
        id: "Status",
        header: t("Status"),
        size: 10,
        minSize: 10,
        cell: (cell) => (
          <OfferStatus
            offerId={cell.row.original.id}
            offerStatus={cell.getValue()}
            rowIndex={cell.row.index}
            openedItemStatusesIndex={openedItemStatusesIndex}
            setOpenedItemStatusesIndex={setOpenedItemStatusesIndex}
            onUpdateStatus={onUpdateBannerStatus}
            fromDate={cell.row.original.from}
            toDate={cell.row.original.to}
          />
        )
      }),
      columnHelper.accessor((row) => row.id, {
        header: "",
        id: "action",
        enableSorting: false,
        size: 3,
        minSize: 3,
        cell: (cell) => (
          <TableItemActionsControl
            actionsList={[
              {
                label: "Edit",
                Icon: <EditIcon fill="#090914" width={18} height={18} />,
                onClick: handleMenuButtonClick(cell, onOpenEdit)
              },
              {
                label: "Delete",
                Icon: <TrashAltIcon fill="#ff0033" width={18} height={18} />,
                labelColor: "#ff0033",
                onClick: handleMenuButtonClick(cell, onOpenDelete)
              }
            ]}
            rowIndex={cell.row.index}
            setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
            openedItemOptionsIndex={openedItemOptionsIndex}
          />
        )
      })
    ],
    [
      handleMenuButtonClick,
      onOpenDelete,
      onOpenEdit,
      onUpdateBannerStatus,
      openedItemOptionsIndex,
      openedItemStatusesIndex,
      isDragDisabled
    ]
  );

  return { columns };
};

export default useBannersTable;
